// FxDirector — event-driven visual effects. Owns NOTHING but uniform
// writes, light tweaks, and decay timers; called once per frame with dt.
// The EventDispatcher feeds it MatchEvents alongside the audio engine.
//
// Particle bursts are deferred to the look-dev pass (step 6); the uniform
// hooks here (goal flash, rink pulse, board-hit pulse, shot heat, FOV
// punch) are the load-bearing FX.

import type { MatchEvent } from "@arcade-hockey/shared";
import type { CameraRig } from "../CameraRig.js";
import { boardsStyle } from "../style/style-schema.js";
import { simToWorldInto } from "../world-mapping.js";
import type { PuckView3D } from "./PuckView3D.js";
import type { RinkView } from "./RinkView.js";

const GOAL_FLASH_DECAY = 2.2; // 1/s exponential decay rates
const RINK_PULSE_DECAY = 1.6;
const HEAT_DECAY = 3.0;
const FOV_PUNCH_DECAY = 4.0;

export class FxDirector {
  constructor(
    private readonly rink: RinkView,
    private readonly puck: PuckView3D,
    private readonly rig: CameraRig | null,
  ) {}

  on(event: MatchEvent): void {
    switch (event.type) {
      case "GoalScored": {
        // The scoring team lights up the goal they scored INTO (the
        // defending team's net at the opposite end).
        const goal = event.teamId === "home" ? this.rink.goals.away : this.rink.goals.home;
        goal.u.flash.value = boardsStyle.goal.flashBoost;
        this.rink.ice.u.rinkPulse.value = 0.6;
        if (this.rig !== null) this.rig.fovPunch = 2.5;
        break;
      }
      case "PuckBoardHit": {
        simToWorldInto(this.rink.trim.u.hitPos.value, event.pos);
        this.rink.trim.u.hitAge.value = 0;
        break;
      }
      case "ShotReleased": {
        this.puck.uniforms.heat.value = 1.2;
        break;
      }
      case "GoalieSave": {
        // Sustained state is snapshot-driven; the spike is a brief heat drop.
        this.puck.uniforms.heat.value = 0.5;
        break;
      }
      default:
        break;
    }
  }

  /** Decay all transient effects. dt in seconds. */
  update(dt: number): void {
    const homeFlash = this.rink.goals.home.u.flash;
    const awayFlash = this.rink.goals.away.u.flash;
    if (homeFlash.value > 0.001) homeFlash.value *= Math.exp(-GOAL_FLASH_DECAY * dt);
    else homeFlash.value = 0;
    if (awayFlash.value > 0.001) awayFlash.value *= Math.exp(-GOAL_FLASH_DECAY * dt);
    else awayFlash.value = 0;

    const pulse = this.rink.ice.u.rinkPulse;
    if (pulse.value > 0.001) pulse.value *= Math.exp(-RINK_PULSE_DECAY * dt);
    else pulse.value = 0;

    this.rink.trim.u.hitAge.value += dt;

    const heat = this.puck.uniforms.heat;
    if (heat.value > 0.001) heat.value *= Math.exp(-HEAT_DECAY * dt);
    else heat.value = 0;

    if (this.rig !== null && this.rig.fovPunch > 0.01) {
      this.rig.fovPunch *= Math.exp(-FOV_PUNCH_DECAY * dt);
    } else if (this.rig !== null) {
      this.rig.fovPunch = 0;
    }
  }
}
